/**
 * Login Screen Component
 * Full-page login form for authenticated sessions
 * Phase 2: Multi-User Auth Foundation
 *
 * SUPABASE INTEGRATION (2025-01-21):
 * - Now tries Supabase auth first (if configured)
 * - Falls back to localStorage auth if Supabase login fails
 * - Shows appropriate error messages for both auth methods
 */

import React, { useState } from 'react';
import { login } from '../../../platform/auth/authService';
import { Mail, Lock, Eye, EyeOff, AlertCircle, Loader2, Cloud, CloudOff } from 'lucide-react';

// Azure auth integration
import { isAzureApiConfigured, azureApiClient } from '../../../lib/azureApiClient';
import { setRBACUser } from '../../../platform/auth/authService';

// Logging utility
import { devError, devLog } from '../../../utils/devLog';

// Storage utilities for vessel initialization
import { legacySet } from '../../../storage/legacyUtils';

// FT-469: clear any stale working-org so a fresh login can't inherit a prior
// session's org context (cross-org isolation).
import { clearWorkingOrg } from '../../../utils/getOrgId';

export const LoginScreen = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // FT-102: Guard against multiple submissions (React StrictMode / rapid clicks)
    if (loading) {
      return;
    }

    setError('');

    // LOCALHOST BYPASS: Skip all auth checks in development (unless explicitly disabled)
    if (process.env.NODE_ENV === 'development' &&
        window.location.hostname === 'localhost' &&
        !process.env.REACT_APP_DISABLE_LOCALHOST_BYPASS) {
      setLoading(true);

      // Create a dev user session without any auth
      const devUser = {
        id: 'usr_dev_localhost_bypass',
        name: 'Dev User (Localhost)',
        email: email || 'dev@localhost.test',
        role: 'super_admin',
        organizationId: '00000000-0000-0000-0000-000000000001',
        organizationName: 'Dev Localhost',
        isActive: true,
        isSuperAdmin: true
      };

      const devSession = {
        userId: devUser.id,
        token: `dev_sess_${Date.now()}`,
        role: 'super_admin',
        userName: devUser.name,
        userEmail: devUser.email,
        expiresAt: Date.now() + (30 * 24 * 60 * 60 * 1000), // 30 days
        isLocalhost: true
      };

      // Call the success callback immediately
      setTimeout(() => {
        onLoginSuccess?.(devUser, devSession);
      }, 500);

      return;
    }

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!password) {
      setError('Please enter your password');
      return;
    }

    setLoading(true);

    try {
      // Azure JWT authentication
      if (isAzureApiConfigured()) {
        try {
          const { user, token } = await azureApiClient.login(email, password);

          // FT-469 (cross-org isolation): a stale sr.working.targetOrgId from a
          // prior impersonation/session must NOT survive into this fresh login,
          // or writes (incl. /sync/push) could be scoped to the wrong org.
          // Logout already wipes this key (authCacheWipe AUTH_KEYS); we clear it
          // here too so login is self-defending regardless of how the prior
          // session ended.
          clearWorkingOrg();

          // CRITICAL: Bridge to RBAC system (architecture requirement)
          setRBACUser(user);

          // FIX: Bridge vessels from auth response to VesselContext storage
          // Architecture compliance: Use legacySet + emit storage event
          if (user.vessels && user.vessels.length > 0) {
            // Store vessels array for VesselContext to find
            legacySet(null, 'vessels', user.vessels);

            // Set first vessel as current (or use user's preferred vessel if available)
            const currentVesselId = user.preferredVesselId || user.vessels[0].id;
            // FT-365 v3.1 hotfix: write currentVesselId as a RAW string, not via
            // legacySet (which JSON.stringifies and double-encodes the UUID).
            // All downstream readers (vesselGuard.js:105, VesselContext.js:154,
            // AuthGate.jsx:213, DefectRecordModal.js:18, etc.) use raw
            // localStorage.getItem and MUST match a plain UUID. Double-encoded
            // values caused a 54k-warning storm from FT-64 vessel-guard and
            // froze the UI in prod (see FT-365 v3.1 hotfix brief).
            // eslint-disable-next-line local/no-localstorage-in-react, no-restricted-syntax -- FT-365 v3.1 raw write matches FT-64 vesselGuard raw read
            localStorage.setItem('currentVesselId', currentVesselId);

            // Emit event for VesselContext synchronization
            window.dispatchEvent(new CustomEvent('sr:storage:set', {
              detail: {
                module: 'vessels',
                key: 'vessels',
                value: user.vessels,
                timestamp: Date.now()
              }
            }));

            devLog(`[AUTH] Initialized ${user.vessels.length} vessel(s) from backend`);
          } else {
            devLog('[AUTH] No vessels in login response - VesselContext will create fallback');
          }

          const appSession = {
            userId: user.id,
            token: token,
            role: user.role,
            userName: user.name,
            userEmail: user.email,
            expiresAt: Date.now() + (7 * 24 * 60 * 60 * 1000), // 7 days (from JWT)
            isAzure: true
          };

          onLoginSuccess?.(user, appSession);
          return;
        } catch (error) {
          if (error.message?.includes('Invalid credentials') || error.message?.includes('401')) {
            setError('Invalid email or password. Please try again.');
          } else {
            setError(error.message || 'Login failed. Please try again.');
          }
          setLoading(false);
          return;
        }
      }

      // localStorage auth ONLY when Supabase is NOT configured (dev/offline mode)
      const result = await login(email, password, rememberMe);

      if (result.success) {
        onLoginSuccess?.(result.user, result.session);
      } else {
        setError(result.error || 'Login failed. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      devError('[Login] Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex flex-col items-center justify-center p-4">
      {/* FT-260: FleetSkipper logo on dark background, above the white form card */}
      <img
        src="/fleetskipper-logo.png"
        alt="FleetSkipper"
        className="h-12 w-auto brightness-0 invert mb-8"
      />
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <p className="text-gray-600">Sign in to your account</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Email Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="admin@vessel.com"
                autoComplete="email"
                autoFocus
                disabled={loading}
              />
            </div>
          </div>

          {/* Password Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Enter your password"
                autoComplete="current-password"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          {/* Remember Me & Forgot Password */}
          <div className="flex items-center justify-between">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                disabled={loading}
              />
              <span className="ml-2 text-sm text-gray-600">
                Remember me for 30 days
              </span>
            </label>
          </div>

          {/* WEEK 4: Password reset success message */}
          {resetSent && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
              <p className="text-sm font-medium">Password reset email sent!</p>
              <p className="text-xs mt-1">Check your inbox for instructions to reset your password.</p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </button>

          {/* DEV MODE: Quick login button (localhost only) */}
          {process.env.NODE_ENV === 'development' && (
            <button
              type="button"
              onClick={async () => {
                setLoading(true);
                setError('');

                try {
                  // Create dev admin if doesn't exist
                  const usersJson = localStorage.getItem('sr.auth.users');
                  let users = usersJson ? JSON.parse(usersJson) : [];

                  let devUser = users.find(u => u.email === 'dev@localhost.test');

                  if (!devUser) {
                    // Import password hashing
                    const { hashPassword } = await import('../../../platform/auth/passwordUtils');
                    const passwordHash = await hashPassword('dev123');

                    // Create dev user with proper hash
                    devUser = {
                      id: 'usr_dev_localhost_001',
                      name: 'Dev Admin',
                      email: 'dev@localhost.test',
                      role: 'SUPER_ADMIN',
                      organizationId: '00000000-0000-0000-0000-000000000001',
                      organizationName: 'Dev Localhost',
                      passwordHash,
                      createdAt: Date.now(),
                      createdBy: 'dev_button',
                      isActive: true,
                      isSuperAdmin: true
                    };
                    users.push(devUser);
                    localStorage.setItem('sr.auth.users', JSON.stringify(users));
                  }

                  // Use the real login function
                  const result = await login('dev@localhost.test', 'dev123', true);

                  if (result.success) {
                    onLoginSuccess?.(result.user, result.session);
                  } else {
                    setError(result.error || 'Dev login failed');
                  }
                } catch (err) {
                  setError('Dev login failed: ' + err.message);
                  devError('[Login] Dev login error:', err);
                } finally {
                  setLoading(false);
                }
              }}
              className="w-full bg-amber-500 text-white py-3 px-4 rounded-lg hover:bg-amber-600 transition-colors font-medium flex items-center justify-center gap-2 mt-3"
            >
              🔧 Dev Login (localhost only)
            </button>
          )}
        </form>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-100">
          <p className="text-xs text-center text-gray-500">
            Safety Management System • Maritime Operations
          </p>
          {/* Cloud sync status indicator */}
          <div className="mt-2 flex items-center justify-center gap-1 text-xs text-gray-400">
            {isAzureApiConfigured() ? (
              <>
                <Cloud className="w-3 h-3" />
                <span>Cloud sync enabled</span>
              </>
            ) : (
              <>
                <CloudOff className="w-3 h-3" />
                <span>Offline mode</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
